import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
// import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Globe from "@insite/mobius/Icons/Globe";

import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
});

const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
    customDiv?: InjectableCss;
    languageNameStyling?: TypographyProps;
    optionStyling?: TypographyProps;
}

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-direction: column;
            position: relative;
            /* width: 120px; */
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 10px;
                        display: flex;
                        flex-direction: row;
                        width: 100%;
                        justify-content: left;
                        margin-left: 0;
                    `,
                    css`
                        margin-top: 10px;
                        display: flex;
                        width: 100%;
                        flex-direction: row;
                        justify-content: left;
                        margin-left: 0;
                    `,
                    css`
                        margin-top: 10px;
                        display: flex;
                        width: 100%;
                        flex-direction: row;
                        justify-content: left;
                        margin-left: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    languageIcon: {
        size: 24,
        src: Globe,
        color: "secondary",
        css: css`
            > svg {
                fill: ${getColor("secondary")};
            }
        `,
    },
    languageSelect: {
        css: css`
            border: none;
            outline: none;
        `,
    },
    customDiv: {
        css: css`
            position: relative;
            display: inline-block;
            .dropdown-content {
                display: none;
                text-align: left;
                position: absolute;
                background-color: #fff;
                width: 90px;
                z-index: 10;
                box-shadow: 3px 7px 16px 3px rgba(0, 0, 0, 0.2);
            }

            &:hover .dropdown-content {
                display: flex;
                flex-direction: column;
                margin-left: -8px;
            }
        `,
    },
    languageNameStyling: {
        css: css`
            text-transform: uppercase;
            text-align: center;
            cursor: pointer;
            white-space: nowrap;
            color: ${getColor("primary")} !important;
            font-size: 14px !important;
            line-height: 25px;
        `,
    },
    optionStyling: {
        css: css`
            text-transform: uppercase;
            font-size: 14px;
            padding: 5px 10px 7px 10px;
            cursor: pointer;
            color: #002f59 !important;
            &:hover {
                color: ${getColor("secondary")};
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

export const LogoImage = styled.img`
    height: 24px;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({ languages, currentLanguage, fields, setLanguage }) => {
    if (!languages || languages.length <= 1) {
        return null;
    }

    React.useEffect(() => {
        const languagesObject: { [key: string]: string } = {};

        languages.forEach(language => {
            const cleanedLanguageCode = language.languageCode.toLowerCase();
            languagesObject[cleanedLanguageCode] = language.id;
        });

        const languagesJSON = JSON.stringify(languagesObject);
        localStorage.setItem("languages", languagesJSON);
    }, []);

    const onChangeLanguage = (languageId: string) => {
        localStorage.setItem("tecLangChangeInProgress", "true");
        setLanguage({ languageId });
    };

    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const lanugaugeCookie = getCookie("tecumseh-com-optout");

    const menuId = "languageMenu";

    const Container: any = ({ children }: any) => <div style={{ maxWidth: 400 }}>{children}</div>;

    return (
        <StyledWrapper {...styles.languageWrapper}>
            {fields.showIcon &&
                (currentLanguage?.imageFilePath ? (
                    <LogoImage src={currentLanguage.imageFilePath} alt="" />
                ) : (
                    <Icon {...styles.languageIcon} />
                ))}
            <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                {translate("Language")}
            </VisuallyHidden>
            <Container>
                <StyledWrapper {...languageMenuStyles.customDiv}>
                    <Typography {...languageMenuStyles.languageNameStyling}>{currentLanguage?.description}</Typography>
                    <StyledWrapper className="dropdown-content" id="lang_dropdown_menu">
                        {languages.map(c => (
                            <Typography
                                {...languageMenuStyles.optionStyling}
                                key={c.id}
                                data-lang={c.languageCode}
                                data-lang-id={c.id}
                                onClick={() => onChangeLanguage(c.id)}
                            >
                                {c.description}
                            </Typography>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            </Container>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "Globe",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
